import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Route, Mail, CheckCircle2 } from "lucide-react";
import { STRATEGIST, mailtoLink } from "@/data/strategist";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StrategyCallDialog({ triggerClassName, triggerLabel = STRATEGIST.ctaLabel, triggerIcon = true, asChildTrigger, source = "strategy_call" }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [agree, setAgree] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", equipment: "", message: "" });

  const valid = form.name.trim() && /\S+@\S+\.\S+/.test(form.email) && form.phone.trim().length >= 7 && agree;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setLoading(true);
    try {
      const lead_id = localStorage.getItem("routehaul_lead_id") || null;
      await axios.post(`${API}/strategy/book`, { ...form, lead_id, source, agreed_terms: true });
      setDone(true);
      toast.success(`Sent to ${STRATEGIST.name.split(" ")[0]} — she'll reach out shortly.`);
    } catch {
      toast.error("Something went wrong. Please email me directly instead.");
    } finally {
      setLoading(false);
    }
  };

  const reset = (v) => {
    setOpen(v);
    if (!v) setTimeout(() => { setDone(false); setAgree(false); }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogTrigger asChild>
        {asChildTrigger || (
          <Button data-testid="strategy-call-trigger" className={triggerClassName}>
            {triggerIcon && <Route className="w-5 h-5 mr-2" />}
            {triggerLabel}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="strategy-call-dialog">
        {done ? (
          <div className="text-center py-4" data-testid="strategy-call-success">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <DialogTitle className="font-heading text-2xl font-black tracking-tight">You're in — talk soon.</DialogTitle>
            <p className="text-slate-600 mt-2">{STRATEGIST.name} received your details and will reach out shortly to map your lanes.</p>
            <a href={mailtoLink()} className="inline-flex items-center gap-1.5 text-sm font-bold text-brand hover:underline mt-4">
              <Mail className="w-4 h-4" /> Or email me right now
            </a>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-1">{STRATEGIST.business}</div>
              <DialogTitle className="font-heading text-2xl font-black tracking-tight">{STRATEGIST.ctaLabel}</DialogTitle>
              <DialogDescription className="leading-relaxed">
                {STRATEGIST.blurb} Leave your details and I'll reach out — or email me directly below.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3 mt-1">
              <div className="space-y-1.5">
                <Label htmlFor="sc-name">Full name</Label>
                <Input id="sc-name" data-testid="strategy-name" value={form.name} onChange={set("name")} placeholder="Jordan Miller" className="h-11" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="sc-email">Email</Label>
                  <Input id="sc-email" data-testid="strategy-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="sc-phone">Phone</Label>
                  <Input id="sc-phone" data-testid="strategy-phone" value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" className="h-11" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sc-equipment">Equipment / lanes you run</Label>
                <Input id="sc-equipment" data-testid="strategy-equipment" value={form.equipment} onChange={set("equipment")} placeholder="e.g. Reefer, West Coast → Southwest" className="h-11" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sc-message">Anything specific? (optional)</Label>
                <textarea id="sc-message" data-testid="strategy-message" value={form.message} onChange={set("message")} rows={2}
                  placeholder="What do you want help with?" className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand/30" />
              </div>
              <label className="flex items-start gap-2.5 text-sm text-slate-600 cursor-pointer pt-1">
                <input type="checkbox" data-testid="strategy-agree" checked={agree} onChange={(e) => setAgree(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-brand shrink-0" />
                <span>
                  I agree to the{" "}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-brand font-bold hover:underline">Terms</a>
                  {" "}&amp;{" "}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand font-bold hover:underline">Privacy Policy</a>
                  , and to be contacted about my request.
                </span>
              </label>
              <Button type="submit" data-testid="strategy-submit" disabled={!valid || loading}
                className="w-full h-12 rounded-full bg-brand hover:bg-brand-hover text-white font-bold disabled:opacity-40">
                {loading ? "Sending…" : STRATEGIST.ctaLabel}
              </Button>
              <a href={mailtoLink()} data-testid="strategy-mailto" className="flex items-center justify-center gap-1.5 text-sm font-bold text-slate-500 hover:text-brand pt-1">
                <Mail className="w-4 h-4" /> Prefer email? Message me directly
              </a>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
